import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ChevronLeft, FileText, Plus, X, EyeOff, RotateCcw, History } from "lucide-react";

const METHODS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank", label: "Bank" },
  { value: "cheque", label: "Cheque" },
  { value: "other", label: "Other" },
];
const todayISO = () => new Date().toISOString().slice(0, 10);
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function VendorLedger({ vendorId, onBack }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [ledger, setLedger] = useState(null);
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [form, setForm] = useState({ po_id: "", amount: "", method: "bank", reference: "", notes: "", paid_on: todayISO() });
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState("");
  const [historyFor, setHistoryFor] = useState(null); // {ref, note}

  const toggleAccounting = async (t) => {
    const nextInclude = !t.include_in_accounts;
    let reason = "";
    if (!nextInclude) {
      reason = window.prompt(`Excluding this ${t.type.toUpperCase()} from accounts.\n\nReason (required — kept in audit log):`);
      if (!reason || !reason.trim()) return;
    } else {
      reason = window.prompt("Reason for including this row back in accounts:", "Re-included after review");
      if (reason === null) return;
    }
    const entityType = t.type === "grn" ? "grn" : "payment";
    setToggling(t.doc_id);
    try {
      await api.post(`/staff/accounting/toggle/${entityType}/${t.doc_id}`, {
        include: nextInclude,
        reason: (reason || "Re-included after review").trim(),
      });
      toast.success(nextInclude ? "Included in accounts" : "Excluded from accounts");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toggle failed");
    } finally { setToggling(""); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [l, p] = await Promise.all([
        api.get(`/staff/vendors/${vendorId}/ledger`),
        api.get(`/staff/purchase-orders`, { params: { vendor_id: vendorId } }),
      ]);
      setLedger(l.data);
      setPos(p.data);
    } catch { toast.error("Could not load ledger"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [vendorId]);

  const eligiblePOs = useMemo(
    () => pos.filter((p) => p.status === "partially_received" || p.status === "received"),
    [pos],
  );

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/staff/vendors/${vendorId}/ledger.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-Vendor-Ledger-${(ledger?.vendor?.name || "vendor").replace(/\s+/g, "_")}-${todayISO()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("PDF download failed"); }
    finally { setDownloading(false); }
  };

  const openPayment = () => {
    if (eligiblePOs.length === 0) {
      toast.error("No POs eligible for payment — post a GRN first.");
      return;
    }
    setForm({ po_id: eligiblePOs[0].id, amount: "", method: "bank", reference: "", notes: "", paid_on: todayISO(), cheque_no: "", cheque_date: "", cheque_bank: "", vendor_signatory: ledger?.vendor?.contact_person || "" });
    setPayOpen(true);
  };

  const savePayment = async (e) => {
    e.preventDefault();
    if (!form.po_id || !form.amount || Number(form.amount) <= 0) {
      toast.error("Pick a PO and enter an amount > 0"); return;
    }
    if (form.method === "cheque" && !form.cheque_no.trim()) {
      toast.error("Cheque number is required when method is Cheque"); return;
    }
    setSaving(true);
    try {
      await api.post("/staff/vendor-payments", {
        po_id: form.po_id,
        amount: Number(form.amount),
        method: form.method,
        reference: form.reference,
        notes: form.notes,
        paid_on: form.paid_on,
        cheque_no: form.cheque_no,
        cheque_date: form.cheque_date,
        cheque_bank: form.cheque_bank,
        vendor_signatory: form.vendor_signatory,
      });
      toast.success("Payment recorded");
      setPayOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record payment");
    } finally { setSaving(false); }
  };

  const downloadAcknowledgement = async (paymentId) => {
    try {
      const { data } = await api.get(`/staff/vendor-payments/${paymentId}/acknowledgement.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `TBB-Payment-Ack-${paymentId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not download acknowledgement");
    }
  };

  if (loading) return <div className="p-6 text-[hsl(var(--ink-soft))]">Loading ledger…</div>;
  if (!ledger) return null;

  const v = ledger.vendor;
  const s = ledger.summary || {};
  const isOverpaid = Number(s.balance_due || 0) < 0;

  return (
    <div data-testid="vendor-ledger">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--ink-soft))] mb-4">
        <ChevronLeft size={14} /> Back to vendors
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Vendor ledger</div>
          <h2 className="mt-1 text-3xl">{v.name}</h2>
          <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">
            {v.phone && <>{v.phone}</>}
            {v.gstin && <> · GSTIN {v.gstin}</>}
            {v.payment_terms && <> · <span className="text-[hsl(var(--brass-2))]">{v.payment_terms}</span></>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="ledger-pay-new" onClick={openPayment} className="btn-brass !py-2.5 !px-5 text-sm">
            <Plus size={14} /> Record payment
          </button>
          <button data-testid="ledger-pdf" onClick={downloadPdf} disabled={downloading} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[hsl(var(--forest))] rounded text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] disabled:opacity-40">
            <FileText size={14} /> {downloading ? "Preparing…" : "Ledger PDF"}
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Tile label="Total invoiced" value={inr(s.total_invoiced)} />
        <Tile label="Total paid" value={inr(s.total_paid)} />
        <Tile label={isOverpaid ? "Overpaid" : "Balance due"} value={inr(Math.abs(s.balance_due))} accent={isOverpaid ? "red" : (Number(s.balance_due) > 0 ? "brass" : "forest")} />
      </div>

      <div className="mt-6 border border-[hsl(var(--rule))]">
        <table className="w-full text-sm">
          <thead className="bg-[hsl(var(--bg-2))] text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] sticky top-0 z-10 shadow-[0_1px_0_hsl(var(--rule))]">
            <tr>
              <th className="p-2 text-left w-28">Date</th>
              <th className="p-2 text-left w-24">Type</th>
              <th className="p-2 text-left">Reference</th>
              <th className="p-2 text-left w-32">PO</th>
              <th className="p-2 text-right w-28">Debit</th>
              <th className="p-2 text-right w-28">Credit</th>
              <th className="p-2 text-right w-28">Balance</th>
              {isAdmin && <th className="p-2 text-right w-16">Acct</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(var(--rule))]">
            {(ledger.transactions || []).map((t, i) => {
              const excluded = t.include_in_accounts === false;
              const rowCls = excluded ? "opacity-40" : "";
              return (
              <tr key={`${t.type}-${t.doc_id}-${i}`} data-testid={`ledger-row-${i}`} className={rowCls}>
                <td className="p-2">{t.date || "—"}</td>
                <td className="p-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                    t.type === "grn" ? "bg-[hsl(var(--brass)/0.15)] text-[hsl(var(--brass-2))]" : "bg-[hsl(148_40%_88%)] text-[hsl(var(--forest))]"
                  }`}>{t.type === "grn" ? "GRN" : "Payment"}</span>
                  {excluded && (
                    <button
                      data-testid={`ledger-history-${i}`}
                      onClick={() => setHistoryFor({ ref: t.ref || t.doc_id, note: t.accounting_note || "" })}
                      title="View accounting history"
                      className="ml-1 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider text-[hsl(6_62%_45%)] hover:underline"
                    >
                      Excluded <History size={9} />
                    </button>
                  )}
                </td>
                <td className="p-2">
                  {t.ref || "—"}
                  {t.type === "payment" && (
                    <>
                      <div className="text-xs text-[hsl(var(--ink-soft))]">{(t.method || "").toUpperCase()}{t.notes ? ` · ${t.notes}` : ""}</div>
                      <button
                        type="button"
                        data-testid={`ledger-ack-${i}`}
                        onClick={() => downloadAcknowledgement(t.doc_id)}
                        title="Download signed acknowledgement PDF"
                        className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[hsl(var(--brass-2))] hover:underline"
                      >
                        <FileText size={10} /> Acknowledgement
                      </button>
                    </>
                  )}
                </td>
                <td className="p-2 font-mono text-xs">{t.po_number || (t.is_direct ? <span className="text-[hsl(var(--brass-2))]">Walk-in</span> : "—")}</td>
                <td className="p-2 text-right">{t.debit ? inr(t.debit) : "—"}</td>
                <td className="p-2 text-right text-[hsl(var(--forest))]">{t.credit ? inr(t.credit) : "—"}</td>
                <td className="p-2 text-right font-semibold">{inr(t.balance)}</td>
                {isAdmin && (
                  <td className="p-2 text-right">
                    <button
                      data-testid={`ledger-toggle-${i}`}
                      onClick={() => toggleAccounting(t)}
                      disabled={toggling === t.doc_id}
                      title={excluded ? "Include in accounts" : "Exclude from accounts (requires reason)"}
                      className={`p-1.5 rounded border ${excluded ? "border-[hsl(var(--forest))] text-[hsl(var(--forest))]" : "border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))] hover:text-[hsl(6_62%_45%)] hover:border-[hsl(6_62%_45%)]"}`}
                    >
                      {excluded ? <RotateCcw size={12} /> : <EyeOff size={12} />}
                    </button>
                  </td>
                )}
              </tr>
              );
            })}
            {ledger.transactions.length === 0 && (
              <tr><td colSpan={isAdmin ? 8 : 7} className="p-10 text-center text-sm text-[hsl(var(--ink-soft))]">
                No transactions yet. GRNs post here as debits; recorded payments post as credits.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(ledger.summary?.excluded_debit > 0 || ledger.summary?.excluded_credit > 0) && (
        <div data-testid="ledger-excluded-note" className="mt-3 text-xs text-[hsl(var(--ink-soft))] italic">
          {inr(ledger.summary.excluded_debit)} debit &amp; {inr(ledger.summary.excluded_credit)} credit are excluded from the totals above.
        </div>
      )}

      {historyFor && (
        <div
          data-testid="ledger-history-modal"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setHistoryFor(null); }}
        >
          <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-lg shadow-xl">
            <div className="p-4 border-b border-[hsl(var(--rule))] flex items-start justify-between">
              <div>
                <div className="eyebrow">Accounting history</div>
                <h2 className="mt-1 text-lg display">{historyFor.ref}</h2>
                <div className="text-xs text-[hsl(var(--ink-soft))] mt-0.5">
                  Timestamped audit trail of every include / exclude toggle on this row.
                </div>
              </div>
              <button data-testid="ledger-history-close" onClick={() => setHistoryFor(null)} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {historyFor.note.trim() ? (
                <ol className="space-y-2 text-sm">
                  {historyFor.note.split("\n").map((line, i) => {
                    // Format: "[YYYY-MM-DD HH:MM:SS · email] INCLUDED/EXCLUDED: reason"
                    const m = line.match(/^\[([^\]]+)\]\s*(INCLUDED|EXCLUDED)?:?\s*(.*)$/);
                    if (!m) return <li key={i} className="text-[hsl(var(--ink-soft))]">{line}</li>;
                    const [_, meta, action, reason] = m;
                    const [ts, by] = meta.split("·").map(s => s.trim());
                    const isExcl = action === "EXCLUDED";
                    return (
                      <li key={i} className="border-l-2 pl-3 border-[hsl(var(--rule))]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${isExcl ? "bg-[hsl(6_62%_45%)]/15 text-[hsl(6_62%_45%)]" : "bg-[hsl(var(--forest))]/15 text-[hsl(var(--forest))]"}`}>
                            {action || "note"}
                          </span>
                          <span className="text-xs text-[hsl(var(--ink-soft))] font-mono">{ts}</span>
                          {by && <span className="text-xs text-[hsl(var(--ink-soft))]">by {by}</span>}
                        </div>
                        <div className="mt-1 text-sm">{reason || "—"}</div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="text-sm text-[hsl(var(--ink-soft))]">No history recorded.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {payOpen && (
        <div data-testid="ledger-pay-modal" className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setPayOpen(false); }}>
          <form onSubmit={savePayment} className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-lg p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow">Record payment</div>
                <h3 className="mt-1 text-xl">{v.name}</h3>
                <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">
                  Allowed only for POs with at least one GRN posted.
                </div>
              </div>
              <button type="button" onClick={() => setPayOpen(false)} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Purchase order *
                <select required data-testid="pay-po" value={form.po_id} onChange={(e) => setForm({ ...form, po_id: e.target.value })} className="tbb-inp mt-1 w-full">
                  {eligiblePOs.map((p) => (
                    <option key={p.id} value={p.id}>{p.po_number} · {inr(p.total)} · {p.status.replace("_", " ")}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Amount (₹) *
                <input required data-testid="pay-amount" type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="tbb-inp mt-1 w-full" autoFocus />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Method *
                <select data-testid="pay-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="tbb-inp mt-1 w-full">
                  {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Reference / UTR / cheque no.
                <input data-testid="pay-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Paid on *
                <input required type="date" data-testid="pay-date" value={form.paid_on} onChange={(e) => setForm({ ...form, paid_on: e.target.value })} className="tbb-inp mt-1 w-full" />
              </label>
              {form.method === "cheque" && (
                <>
                  <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                    Cheque number *
                    <input
                      required
                      data-testid="pay-cheque-no"
                      value={form.cheque_no}
                      onChange={(e) => setForm({ ...form, cheque_no: e.target.value })}
                      className="tbb-inp mt-1 w-full"
                      placeholder="e.g. 000123"
                    />
                  </label>
                  <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                    Cheque date
                    <input
                      type="date"
                      data-testid="pay-cheque-date"
                      value={form.cheque_date}
                      onChange={(e) => setForm({ ...form, cheque_date: e.target.value })}
                      className="tbb-inp mt-1 w-full"
                    />
                  </label>
                  <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                    Drawn on (Bank / branch)
                    <input
                      data-testid="pay-cheque-bank"
                      value={form.cheque_bank}
                      onChange={(e) => setForm({ ...form, cheque_bank: e.target.value })}
                      className="tbb-inp mt-1 w-full"
                      placeholder="e.g. HDFC Bank, Coonoor branch"
                    />
                  </label>
                </>
              )}
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Vendor signatory (name on acknowledgement)
                <input
                  data-testid="pay-vendor-signatory"
                  value={form.vendor_signatory}
                  onChange={(e) => setForm({ ...form, vendor_signatory: e.target.value })}
                  className="tbb-inp mt-1 w-full"
                  placeholder="Who will sign for the vendor?"
                />
              </label>
              <label className="col-span-2 text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                Notes
                <textarea rows={2} data-testid="pay-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="tbb-inp mt-1 w-full resize-none" />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setPayOpen(false)} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
              <button data-testid="pay-save" disabled={saving} className="btn-brass !py-2.5 !px-5 text-sm">{saving ? "Saving…" : "Record payment"}</button>
            </div>
            <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
          </form>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, accent = "ink" }) {
  const color = { ink: "text-[hsl(var(--ink))]", forest: "text-[hsl(var(--forest))]", brass: "text-[hsl(var(--brass-2))]", red: "text-[hsl(6_62%_45%)]" }[accent] || "text-[hsl(var(--ink))]";
  return (
    <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))] p-4">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}
